import { SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { Control, FieldValues, UseFormReset, useFormState } from 'react-hook-form';
import { updateProductsPricing } from 'src/api/updateProductsPricing/updateProductsPricing';
import { mapFieldValueToPrice } from './utils';
import { BUNDLE_MODIFIER_UPDATE_MSG } from './constants';

interface UseSavePriceOverridesArgs {
  priceSchemeId: string;
  queryKey: QueryKey;
  control: Control<FieldValues>;
  reset: UseFormReset<FieldValues>;
}

/**
 * Builds the submit handler shared by the product and standalone option-group
 * override tables: it sends only the dirty modifier prices to the pricing API,
 * refetches the affected list and surfaces success / failure to the user.
 */
export const useSavePriceOverrides = ({ priceSchemeId, queryKey, control, reset }: UseSavePriceOverridesArgs) => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { dirtyFields } = useFormState({ control });

  return async (fieldValues: FieldValues) => {
    try {
      const dirtyKeys = new Set(Object.keys(dirtyFields));
      if (dirtyKeys.size === 0) return;
      const values = Object.entries(fieldValues).filter(([key]) => dirtyKeys.has(key));

      const payload = values.map(mapFieldValueToPrice);
      await updateProductsPricing(priceSchemeId, payload);

      queryClient.invalidateQueries({ queryKey });
      reset(fieldValues, { keepValues: false });

      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: BUNDLE_MODIFIER_UPDATE_MSG.SUCCESS,
      });
    } catch (error) {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: error instanceof Error ? error.message : BUNDLE_MODIFIER_UPDATE_MSG.FAILURE,
      });
    }
  };
};
